import React, { useState } from 'react';
import { Alert, Button, Col, Input, Popconfirm, Row, Space, Switch, Table } from 'antd';
import { DeleteOutlined, LinkOutlined, ReloadOutlined, SearchOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { ClientSupplier } from 'api/useClient';
import type { MappingTableProps } from '../types';

/**
 * Displays the supplier list for the selected client in a paginated table.
 *
 * Each row shows the supplier ID, name, and the mapped vendor's ID, name, and domain.
 * Rows with an existing vendor mapping offer a guarded "Remove Map" action;
 * unmapped rows offer a "Map Vendor" action that opens the mapping modal.
 *
 * A toggle above the table controls whether all suppliers or only unmapped ones
 * are shown. A "Bulk Match" button triggers auto-matching of all unmapped suppliers.
 *
 * Wrapped with `React.memo` to prevent unnecessary re-renders when sibling
 * modal state (e.g. `isMapModalOpen`) changes while the supplier list is unchanged.
 * @param {MappingTableProps} props - Component props.
 * @returns {React.ReactElement} The suppliers table with filter toggle and bulk match.
 */
const PAGE_SIZE_KEY = 'supplierMapping.pageSize';

const MappingTable: React.FC<MappingTableProps> = ({
  suppliers,
  loading,
  onMapVendor,
  onRemoveSupplier,
  showUnmappedOnly,
  onToggleUnmappedFilter,
  onRefresh,
  onBulkMatch,
  isBulkMatching,
  bulkMatchSummary,
  onDismissBulkSummary
}) => {
  const [pageSize, setPageSize] = useState<number>(() => Number(sessionStorage.getItem(PAGE_SIZE_KEY)) || 50);
  const [filterString, setFilterString] = useState<string>('');

  const filteredSuppliers = filterString
    ? suppliers.filter((s) => {
        const q = filterString.slice(0, 100).toLowerCase();
        return (
          s.supplierId?.toString().toLowerCase().includes(q) ||
          s.supplierName?.toLowerCase().includes(q) ||
          s.supplierDomain?.toLowerCase().includes(q) ||
          s.vendor?.id?.toString().toLowerCase().includes(q) ||
          s.vendor?.name?.toLowerCase().includes(q) ||
          s.vendor?.domain?.toLowerCase().includes(q)
        );
      })
    : suppliers;

  const columns = [
    {
      title: 'Supplier ID',
      dataIndex: 'supplierId',
      key: 'supplierId'
    },
    {
      title: 'Supplier Name',
      dataIndex: 'supplierName',
      key: 'supplierName'
    },
    {
      title: 'Supplier Domain',
      key: 'supplierDomain',
      render: (_: unknown, record: ClientSupplier) => record.supplierDomain ?? '—'
    },
    {
      title: 'Vendor ID',
      key: 'vendorId',
      render: (_: unknown, record: ClientSupplier) => record.vendor?.id ?? '—'
    },
    {
      title: 'Vendor Name',
      key: 'vendorName',
      render: (_: unknown, record: ClientSupplier) => record.vendor?.name ?? '—'
    },
    {
      title: 'Vendor Domain',
      key: 'vendorDomain',
      render: (_: unknown, record: ClientSupplier) => record.vendor?.domain ?? '—'
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      render: (_: unknown, record: ClientSupplier) =>
        record.vendor ? (
          <Popconfirm
            title="Remove this vendor mapping?"
            onConfirm={() => onRemoveSupplier(record)}
            okText="Remove"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button danger type="primary" icon={<DeleteOutlined />} disabled={isBulkMatching}>
              Remove Map
            </Button>
          </Popconfirm>
        ) : (
          <Button type="default" icon={<LinkOutlined />} onClick={() => onMapVendor(record)} disabled={isBulkMatching}>
            Map Vendor
          </Button>
        )
    }
  ];

  return (
    <Row className="mt-6">
      <Col span={24}>
        <Row justify="space-between" align="middle" className="mb-4">
          <Col>
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">Filters</span>
              <Switch
                checked={!showUnmappedOnly}
                onChange={onToggleUnmappedFilter}
                checkedChildren="All Suppliers"
                unCheckedChildren="Unmapped Suppliers Only"
              />
              <Input
                placeholder="Filter by anything"
                prefix={<SearchOutlined />}
                value={filterString}
                size="large"
                allowClear
                onChange={(e) => setFilterString(e.target.value)}
                style={{ width: 220 }}
              />
            </div>
          </Col>
          <Col>
            <Space>
              <Button type="default" icon={<ReloadOutlined />} onClick={onRefresh} loading={loading}>
                Refresh Table
              </Button>
              <Button type="primary" icon={<ThunderboltOutlined />} onClick={onBulkMatch} loading={isBulkMatching}>
                Bulk Match
              </Button>
            </Space>
          </Col>
        </Row>
        {bulkMatchSummary && (
          <Alert
            type="info"
            closable
            onClose={onDismissBulkSummary}
            message="Bulk Match Complete"
            description={`${bulkMatchSummary.autoMappedCount} auto-mapped, ${bulkMatchSummary.needsReviewCount} need review, ${bulkMatchSummary.noMatchCount} no match found.`}
            className="mb-4"
          />
        )}
        <Table<ClientSupplier>
          rowKey="id"
          loading={loading}
          dataSource={filteredSuppliers}
          pagination={{
            position: ['topRight', 'bottomRight'],
            showSizeChanger: true,
            pageSize,
            onShowSizeChange: (_, size) => {
              setPageSize(size);
              sessionStorage.setItem(PAGE_SIZE_KEY, String(size));
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`
          }}
          columns={columns}
        />
      </Col>
    </Row>
  );
};

MappingTable.displayName = 'MappingTable';

export default React.memo(MappingTable);
